/**
 * HEIC/HEIF (Apple photo format) helpers for upload UIs.
 *
 * Uploads: HEIC files are sent to the API as-is; the API decodes them and
 * stores/serves WebP, so published posts, events, blogs and profile images are
 * always displayed as WebP.
 *
 * Previews: only Safari can render HEIC natively. Elsewhere we decode the file
 * in the browser (heic2any, lazy-loaded so it never touches the main bundle or
 * SSR) into a temporary JPEG used purely for the local preview / cropper.
 */

/** Extra values for `<input type="file" accept="...">` lists. */
export const HEIC_ACCEPT = 'image/heic,image/heif,.heic,.heif';

const HEIC_MIME_RE = /^image\/(heic|heif)(-sequence)?$/i;
const HEIC_EXT_RE = /\.(heic|heif|hif)$/i;

export const isHeicFile = (file: { name?: string; type?: string } | null | undefined): boolean =>
  !!file && (HEIC_MIME_RE.test(file.type ?? '') || HEIC_EXT_RE.test(file.name ?? ''));

let nativeHeicSupport: Promise<boolean> | null = null;

/** Whether this browser can decode HEIC itself (Safari 17+). Checked once. */
const browserDecodesHeic = (file: Blob): Promise<boolean> => {
  if (!nativeHeicSupport) {
    nativeHeicSupport = (async () => {
      if (typeof createImageBitmap !== 'function') return false;
      try {
        const bitmap = await createImageBitmap(file);
        bitmap.close?.();
        return true;
      } catch {
        return false;
      }
    })();
  }
  return nativeHeicSupport;
};

const previewCache = new WeakMap<Blob, Promise<Blob>>();

/**
 * Returns a Blob the browser can display for preview purposes. Non-HEIC files
 * are returned unchanged. Rejects if the HEIC file can't be decoded.
 */
export const toPreviewableBlob = (file: File | Blob): Promise<Blob> => {
  if (!isHeicFile(file as File)) return Promise.resolve(file);

  const cached = previewCache.get(file);
  if (cached) return cached;

  const result = (async () => {
    if (await browserDecodesHeic(file)) return file;
    const { default: heic2any } = await import('heic2any');
    const converted = await heic2any({ blob: file, toType: 'image/jpeg', quality: 0.9 });
    return Array.isArray(converted) ? converted[0] : converted;
  })();

  previewCache.set(file, result);
  result.catch(() => previewCache.delete(file));
  return result;
};

/**
 * Drop-in for `reader.readAsDataURL(file)` that transparently swaps HEIC for
 * a displayable copy first. Decode failures surface through `reader.onerror`.
 */
export const readAsPreviewDataUrl = (reader: FileReader, file: File | Blob): void => {
  if (!isHeicFile(file as File)) {
    reader.readAsDataURL(file);
    return;
  }
  toPreviewableBlob(file)
    .then(blob => reader.readAsDataURL(blob))
    .catch(() => reader.onerror?.call(reader, new ProgressEvent('error') as ProgressEvent<FileReader>));
};

/** Object-URL variant for previews built with `URL.createObjectURL`. */
export const createPreviewObjectUrl = async (file: File | Blob): Promise<string> =>
  URL.createObjectURL(await toPreviewableBlob(file));
